import { CartUpdateEvent } from '@theme/events';

class GtgCartDrawerUpsell extends HTMLElement {
  #busy = false;

  #abortController;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.button?.addEventListener('click', this.#handleAdd, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  get button() {
    return this.querySelector('[data-gtg-cart-upsell-button]');
  }

  get status() {
    return this.querySelector('[data-gtg-cart-upsell-status]');
  }

  get sectionIds() {
    return Array.from(
      new Set(
        Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
          .map((component) => component.dataset.sectionId)
          .filter(Boolean)
      )
    );
  }

  #cartEndpoint(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${path}`;
  }

  async #requestCart(path, options = {}) {
    const response = await fetch(this.#cartEndpoint(path), {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (_) {
      payload = null;
    }

    if (!response.ok || payload?.status) {
      throw new Error(payload?.description || payload?.message || `Cart request failed (${response.status})`);
    }

    return payload;
  }

  #postCart(path, body) {
    return this.#requestCart(path, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async #publishCart(cart, sections = {}) {
    document.dispatchEvent(
      new CartUpdateEvent(cart, 'gtg-cart-metal-tin-upsell', {
        source: 'gtg-cart-metal-tin-upsell',
        itemCount: Number(cart?.item_count) || 0,
        variantId: String(this.dataset.variantId || ''),
        sections,
      })
    );

    await new Promise((resolve) => requestAnimationFrame(resolve));
    document.querySelector('cart-drawer-component')?.open?.();
  }

  #showStatus(message) {
    if (this.status) this.status.textContent = message;
  }

  #setBusy(isBusy) {
    this.#busy = isBusy;
    if (!this.button) return;

    this.button.disabled = isBusy;
    this.button.setAttribute('aria-busy', String(isBusy));

    const action = this.querySelector('[data-gtg-cart-upsell-action]');
    if (action) action.textContent = isBusy ? 'ADDING…' : 'ADD +';
  }

  #handleAdd = async () => {
    if (this.#busy) return;

    const variantId = Number(this.dataset.variantId);
    const offerId = this.dataset.offerId || 'gtg1can';
    const offerLabel = this.dataset.offerLabel || 'GTG1CAN';
    const offerToken = this.dataset.offerToken || '';
    if (!variantId) {
      this.#showStatus('The metal tin is temporarily unavailable.');
      return;
    }

    this.#setBusy(true);
    this.#showStatus('');
    let itemWasAdded = false;

    try {
      await this.#postCart('cart/add.js?kaching_bundles=true', {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _gtg_offer: offerLabel,
              _gtg_offer_id: offerId,
              _gtg_offer_label: offerLabel,
              _gtg_offer_role: 'order-bump',
              _gtg_offer_token: offerToken,
            },
          },
        ],
      });
      itemWasAdded = true;

      const sectionIds = this.sectionIds;
      const cartUpdate = await this.#postCart('cart/update.js', {
        ...(sectionIds.length
          ? {
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }
          : {}),
      });

      const addedLine = cartUpdate?.items?.find(
        (item) =>
          Number(item.variant_id) === variantId &&
          item.properties?._gtg_offer_role === 'order-bump' &&
          item.properties?._gtg_offer_token === offerToken
      );
      if (!addedLine) throw new Error('The metal tin could not be verified in your cart.');

      await this.#publishCart(cartUpdate, cartUpdate.sections || {});
    } catch (error) {
      console.error('GTG cart metal-tin upsell failed:', error);

      if (itemWasAdded) {
        try {
          const cart = await this.#requestCart('cart.js');
          await this.#publishCart(cart);
          this.#showStatus('The tin was added. Refresh the cart if it does not appear right away.');
        } catch (refreshError) {
          console.error('GTG cart refresh failed:', refreshError);
          this.#showStatus('The tin was added, but the cart could not refresh. Please reopen the cart.');
        }
      } else {
        this.#showStatus(error?.message || 'The tin could not be added. Please try again.');
      }
    } finally {
      if (this.isConnected) this.#setBusy(false);
    }
  };
}

if (!customElements.get('gtg-cart-drawer-upsell')) {
  customElements.define('gtg-cart-drawer-upsell', GtgCartDrawerUpsell);
}
